/**
 * Date formatting utilities to ensure consistent date display across the application
 * All dates display as DD/MM/YYYY in Malaysia time (GMT+8).
 *
 * The API returns instants in UTC. Formatting through the browser clock showed
 * the viewer timezone instead of Malaysia time, so a record saved at 6pm local
 * read differently depending on where it was opened. Pin the zone instead.
 */

const MY_TIMEZONE = "Asia/Kuala_Lumpur";

/** Break an instant into Malaysia-time parts we can assemble by hand. */
const malaysiaParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: MY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((part) => part.type === type)?.value ?? "";
  return {
    day: get("day"),
    month: get("month"),
    year: get("year"),
    // Intl renders midnight as 24 in some engines
    hour: get("hour") === "24" ? "00" : get("hour"),
    minute: get("minute"),
  };
};

export const formatDate = (value) => {
  const p = malaysiaParts(value);
  if (!p) return "—";
  return `${p.day}/${p.month}/${p.year}`;
};

export const formatDateTime = (value) => {
  const p = malaysiaParts(value);
  if (!p) return "—";
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
};

export const formatDateTimeAMPM = (value) => {
  const p = malaysiaParts(value);
  if (!p) return "—";
  const h24 = Number(p.hour);
  const ampm = h24 >= 12 ? "pm" : "am";
  const hour = h24 % 12 || 12;
  return `${p.day}/${p.month}/${p.year}, ${hour}:${p.minute} ${ampm}`;
};

export const formatDateGB = (value) => {
  const p = malaysiaParts(value);
  if (!p) return "";
  return `${p.day}/${p.month}/${p.year}`;
};
